import { Link, Navigate, useParams } from 'react-router-dom';
import CourseCard from '../components/Coursecard';
import LuxCta from '../components/lux/LuxCta';
import LuxPageHero from '../components/lux/LuxPageHero';
import LuxReveal from '../components/lux/LuxReveal';
import { Icon } from '../components/ui/Icons';
import { allCourses, getCourseBySlug, getCourseFaqs } from '../data/courses';

export default function CourseDetail() {
  const { slug } = useParams();
  const course = getCourseBySlug(slug);

  if (!course) return <Navigate to="/courses" replace />;

  const faqs = getCourseFaqs(course);
  const relatedCourses = allCourses
    .filter(item => item.slug !== course.slug && item.category === course.category)
    .slice(0, 3);
  const relatedSlugs = new Set(relatedCourses.map(item => item.slug));
  const fallbackRelated = allCourses
    .filter(item => item.slug !== course.slug && !relatedSlugs.has(item.slug))
    .slice(0, 3 - relatedCourses.length);
  const visibleRelated = [...relatedCourses, ...fallbackRelated].slice(0, 3);

  const learningPoints = [
    `${course.title} fundamentals with job-focused practice`,
    ...(course.features ?? []),
    'Certification guidance, resume support and interview preparation',
  ];

  return (
    <>
      <LuxPageHero
        label={`${course.category} Course in Noida`}
        title={course.title}
        highlight="Training"
        description={course.seoDescription}
        cta={{ to: '/admission', label: 'Apply Free' }}
      />

      <section className="lux-page-section">
        <div className="container-wide">
          <div style={{ marginBottom: 28, color: 'var(--text-muted)', fontSize: 13 }}>
            <Link to="/">Home</Link>
            <span style={{ margin: '0 8px' }}>/</span>
            <Link to="/courses">Courses</Link>
            <span style={{ margin: '0 8px' }}>/</span>
            <span style={{ color: 'var(--text)' }}>{course.title}</span>
          </div>

          <div className="lux-grid-2">
            <LuxReveal>
              <div>
                <p className="lux-statement__eyebrow">Course overview</p>
                <h2 className="video-gallery__title" style={{ fontSize: 'clamp(1.8rem, 4vw, 2.7rem)', marginTop: 12 }}>
                  Practical <em style={{ fontStyle: 'italic', color: 'var(--accent-soft)' }}>{course.category}</em> training
                </h2>
                <p style={{ color: 'var(--text-secondary)', marginTop: 20, lineHeight: 1.85 }}>
                  {course.seoDescription} The program is designed for students and working professionals who want practical skills, clear guidance and better career opportunities.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginTop: 28 }}>
                  {[
                    ['Duration', course.duration, 'clock'],
                    ['Fee', course.price ?? 'Counselling', 'award'],
                    ['Location', 'Sector-3 Noida', 'mapPin'],
                    ['Support', 'Placement', 'briefcase'],
                  ].map(([label, value, icon]) => (
                    <div key={label} className="lux-card" style={{ padding: 18 }}>
                      <div style={{ color: 'var(--accent)', marginBottom: 10 }}><Icon name={icon} size={18} /></div>
                      <div style={{ color: 'var(--text-muted)', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase' }}>{label}</div>
                      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginTop: 4 }}>{value}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 32 }}>
                  <Link to="/admission" className="btn-lux btn-lux--fill" style={{ color: '#030712' }}>Apply Free</Link>
                  <a href="tel:+918851868521" className="btn-lux btn-lux--line" style={{ color: 'var(--text)' }}>
                    <Icon name="phone" size={16} /> Call 88518 68521
                  </a>
                </div>
              </div>
            </LuxReveal>

            <LuxReveal delay={0.12} x={28}>
              <div style={{ border: '1px solid var(--border)', overflow: 'hidden', background: 'var(--surface)' }}>
                <img
                  src={course.img}
                  alt={`${course.title} training in Noida at Bharat Industrial Training`}
                  loading="eager"
                  style={{ width: '100%', height: 420, objectFit: 'cover' }}
                  onError={e => { e.target.style.opacity = '0.35'; }}
                />
              </div>
            </LuxReveal>
          </div>
        </div>
      </section>

      <section className="lux-page-section" style={{ background: 'var(--bg-elevated)' }}>
        <div className="container-wide lux-grid-2">
          <LuxReveal>
            <p className="lux-statement__eyebrow">What you learn</p>
            <h2 className="video-gallery__title" style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', marginTop: 12 }}>
              Skills that support <em style={{ fontStyle: 'italic', color: 'var(--accent-soft)' }}>real jobs</em>
            </h2>
          </LuxReveal>
          <LuxReveal delay={0.1}>
            <div className="lux-card">
              <ul style={{ display: 'grid', gap: 14 }}>
                {learningPoints.map(point => (
                  <li key={point} style={{ display: 'flex', gap: 12, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                    <span className="course-card__check" style={{ marginTop: 4 }}><Icon name="check" size={10} /></span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </LuxReveal>
        </div>
      </section>

      <section className="lux-page-section">
        <div className="container-wide">
          <LuxReveal>
            <p className="lux-statement__eyebrow" style={{ textAlign: 'center' }}>Course FAQ</p>
            <h2 className="video-gallery__title" style={{ textAlign: 'center', marginBottom: 40 }}>
              Common <em style={{ fontStyle: 'italic', color: 'var(--accent-soft)' }}>questions</em>
            </h2>
          </LuxReveal>
          <div style={{ display: 'grid', gap: 16, maxWidth: 860, margin: '0 auto' }}>
            {faqs.map((faq, i) => (
              <LuxReveal key={faq.question} delay={i * 0.05}>
                <div className="lux-card">
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', marginBottom: 10 }}>{faq.question}</h3>
                  <p style={{ color: 'var(--text-secondary)', lineHeight: 1.75 }}>{faq.answer}</p>
                </div>
              </LuxReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="lux-page-section" style={{ background: 'var(--bg-elevated)' }}>
        <div className="container-wide">
          <LuxReveal>
            <p className="lux-statement__eyebrow" style={{ textAlign: 'center' }}>Related programs</p>
            <h2 className="video-gallery__title" style={{ textAlign: 'center', marginBottom: 36 }}>
              Explore more <em style={{ fontStyle: 'italic', color: 'var(--accent-soft)' }}>courses</em>
            </h2>
          </LuxReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
            {visibleRelated.map(item => <CourseCard key={item.slug} {...item} />)}
          </div>
        </div>
      </section>

      <LuxCta
        eyebrow={course.category}
        title="Start this"
        highlight="course"
        primary={{ to: '/admission', label: 'Apply Free' }}
        secondary={{ to: '/contact', label: 'Contact Us' }}
      />
    </>
  );
}
